import { Attributes, Observability } from '@mews-framework/observability';
import { LoggerAdapter } from './types';

/**
 * Transforms legacy log parameters into structured LogAttributes for Coralogix
 *
 * Maps old format:
 *   loggerService.info(type, message, { key1: value1, key2: value2 })
 *
 * To new format:
 *   {
 *     'mews.message': 'message',
 *     'mews.legacy_log.type': 'type_value',
 *     'mews.legacy_log.key1': 'value1',
 *     'mews.legacy_log.key2': 'value2'
 *   }
 *
 * @param type - The log type/category (e.g., 'BillingAction', 'PaymentProcessing')
 * @param message - Human-readable log message
 * @param data - Additional context data to be included as attributes
 * @returns Structured LogAttributes for Coralogix
 *
 * Note: This uses a temporary 'legacy_log' domain for backward compatibility.
 * Teams should improve domain mapping to proper business domains over time.
 */
const createLogAttributes = (
    type: string,
    message: string,
    data: Record<string, string | number | boolean | null | undefined> = {}
): Record<string, string> => {
    const attributes: Record<string, string> = {
        'mews.message': message,
        'mews.legacy_log.type': type,
    };

    for (const [key, value] of Object.entries(data)) {
        attributes[`mews.legacy_log.${key}`] = `${value}`;
    }

    return attributes;
};

export const createCoralogixLoggerAdapter = (): LoggerAdapter => ({
    info: (type: string, message: string, data = {}) => {
        Observability.addLog(createLogAttributes(type, message, data), {
            severity: Attributes.LogLevel.Info,
        });
    },

    warn: (type: string, message: string, data = {}) => {
        Observability.addLog(createLogAttributes(type, message, data), {
            severity: Attributes.LogLevel.Warn,
        });
    },

    error: (type: string, message: string, data = {}) => {
        Observability.addLog(createLogAttributes(type, message, data), {
            severity: Attributes.LogLevel.Error,
        });
    },

    log: (type: string, message: string, data = {}) => {
        Observability.addLog(createLogAttributes(type, message, data), {
            severity: Attributes.LogLevel.Info,
        });
    },
});

export default createCoralogixLoggerAdapter;
